/**
 * ============================================================================
 * ✈️ Aircraft Type Icon Lookup (ICAO type code → top-view silhouette SVG)
 * ============================================================================
 * Source: 182 detailed technical top-view silhouettes from aircraft-icons.json
 * Enhanced with comprehensive alias dictionary and family heuristics so that
 * all aircraft types (Helicopters, Turboprops, Bizjets, Fighters, Light GA)
 * render with their authentic, beautiful detailed silhouettes!
 * ============================================================================
 */

import iconsByCode from '../assets/aircraft-icons.json';

const FALLBACK_CODE = 'UNIDENTIFIED';

// Comprehensive ICAO Type Code Alias Map to detailed silhouettes
export const ICAO_ALIASES = {
  // --- Airbus Family ---
  'A319': 'A320', 'A318': 'A320', 'A320': 'A320', 'A321': 'A321',
  'A19N': 'A19N', 'A20N': 'A20N', 'A21N': 'A21N',
  'A330': 'A333', 'A332': 'A332', 'A333': 'A333', 'A338': 'A338', 'A339': 'A339',
  'A340': 'A343', 'A350': 'A359', 'A359': 'A359', 'A35K': 'A35K', 'A380': 'A388',
  'A220': 'BCS3', 'A221': 'BCS1', 'A223': 'BCS3',

  // --- Boeing Family ---
  'B731': 'B733', 'B732': 'B733', 'B736': 'B737', 'B37M': 'B38M',
  'B777': 'B77W', 'B787': 'B789',

  // --- Regional Jets & Turboprops ---
  'AT72': 'AT75', 'AT73': 'AT75', 'AT76': 'AT75',
  'AT42': 'AT45', 'AT43': 'AT45', 'AT46': 'AT45',
  'DH8A': 'DH8C', 'DH8B': 'DH8C', 'Q400': 'DH8D',
  'E190': 'E195', 'E290': 'E195', 'E295': 'E195', 'E175': 'E170',

  // --- Helicopters (ครบทุกตระกูลสากล) ---
  'B412': 'UH1', 'B212': 'UH1', 'B205': 'UH1', 'B214': 'UH1',
  'B429': 'EC45', 'B427': 'EC45', 'B430': 'EC45',
  'B06': 'EC20', 'B407': 'EC20', 'B505': 'EC20',
  'H125': 'EC35', 'AS50': 'EC35', 'AS55': 'EC35',
  'H135': 'EC35', 'H145': 'EC45', 'BK11': 'EC45',
  'H160': 'AS65', 'EC55': 'AS65',
  'H225': 'AS32', 'EC25': 'AS32', 'AS33': 'AS32',
  'S92': 'H60', 'S76': 'H60', 'S70': 'H60', 'UH60': 'H60',
  'AW139': 'EC45', 'A139': 'EC45', 'A189': 'EC45', 'A169': 'EC45',
  'AW109': 'EC35', 'A109': 'EC35', 'A119': 'EC35',
  'R66': 'R44', 'R22': 'R44',
  'CH47': 'H47', 'AH64': 'H64',
  'MI8': 'MI24', 'MI17': 'MI24', 'KA27': 'H60', 'KA32': 'H60',

  // --- Business Jets ---
  'GLF4': 'GLF6', 'GLF5': 'GLF6', 'GLEX': 'GL5T', 'GL7T': 'GL5T',
  'GA5C': 'GL5T', 'GA6C': 'GL5T', 'GALX': 'GLF6',
  'FA8X': 'FA7X', 'F900': 'FA7X', 'F2TH': 'FA7X', 'FA50': 'FA7X',
  'H25B': 'LJ35', 'LJ40': 'LJ35', 'LJ45': 'LJ35', 'LJ60': 'LJ35',
  'C56X': 'C750', 'C68A': 'C750', 'C25A': 'C25B', 'C25C': 'C25B',
  'C525': 'C25B', 'C560': 'C750', 'C680': 'C750', 'C510': 'C25B',
  'CL30': 'CRJ2', 'CL35': 'CRJ2', 'CL60': 'CRJ2',
  'E35L': 'E35L', 'E55P': 'E35L', 'E550': 'E35L', 'E500': 'E35L',
  'HA4T': 'C25B', 'PC24': 'C25B',

  // --- General Aviation & Light Aircraft ---
  'C150': 'C172', 'C152': 'C172', 'C170': 'C172', 'C182': 'C172', 'C210': 'C172',
  'C206': 'C208', 'PA28': 'P28A', 'PA32': 'P28A', 'PA34': 'PA46', 'PA44': 'PA46',
  'SR20': 'SR22', 'SF50': 'SR22', 'DA20': 'DA42', 'DA40': 'DA42', 'DA62': 'DA42',
  'SLG4': 'C172', 'SLG2': 'C172',

  // --- Military Fighters & Transport ---
  'F16A': 'F16', 'F16B': 'F16', 'F16C': 'F16', 'F16D': 'F16',
  'F15E': 'F15', 'FA18': 'F18H', 'F35A': 'F35', 'F35B': 'F35',
  'F5E': 'F5', 'F5F': 'F5', 'GRIP': 'SB39', 'JAS39': 'SB39',
  'TYPH': 'EUFI', 'SU27': 'F15', 'SU30': 'F15', 'SU35': 'F15',
  'MIG29': 'F16', 'T50': 'F16', 'L39': 'L159', 'L100': 'C130',

  // --- Drone / Glider / Balloon ---
  'DRON': 'UAV', 'UAV': 'UAV', 'MQ9': 'UAV', 'RQ4': 'UAV', 'TB2': 'UAV', 'HERM': 'UAV', 'SCAN': 'UAV',
  'GLID': 'GLID', 'SF25': 'GLID', 'BLNP': 'BALL', 'HBAL': 'BALL'
};

const normalizedCache = new Map();

/**
 * Normalizes the 80x80 technical SVG viewBox to prevent small aircraft
 * (Cessna, Robinson, Eurocopter, Bell) from becoming invisible 4px specks.
 * Retains natural proportional hierarchy while ensuring every aircraft is
 * clearly visible and beautifully detailed on screen.
 */
function normalizeViewBox(rawSvg) {
  if (!rawSvg) return '';
  if (normalizedCache.has(rawSvg)) {
    return normalizedCache.get(rawSvg);
  }

  const vbMatch = rawSvg.match(/viewBox=["']([^"']+)["']/);
  if (!vbMatch) {
    normalizedCache.set(rawSvg, rawSvg);
    return rawSvg;
  }

  const [minX, minY, w, h] = vbMatch[1].split(/\s+/).map(Number);
  if (w !== 80 || h !== 80) {
    normalizedCache.set(rawSvg, rawSvg);
    return rawSvg;
  }

  const cx = minX + 40;
  const cy = minY + 40;

  // S is estimated physical span in meters (half-span * 2)
  const S = Math.max(8, cx * 2);

  // Perception-balanced box size:
  // Small GA/Helis (S~10-14m) -> D~21-25 (scales up ~3.5x to ~22-25px)
  // Medium Jets (S~34-36m) -> D~42-44 (scales up ~1.8x to ~29-31px)
  // Widebodies (S~60-65m) -> D~65-69 (scales up ~1.2x to ~33-35px)
  // Super Heavy (S~80m) -> D~80 (1.0x to 38px)
  const D = Math.max(20, Math.min(80, Math.round(12 + S * 0.88)));

  const newMinX = Number((cx - D / 2).toFixed(2));
  const newMinY = Number((cy - D / 2).toFixed(2));

  const result = rawSvg.replace(vbMatch[0], `viewBox="${newMinX} ${newMinY} ${D} ${D}"`);
  normalizedCache.set(rawSvg, result);
  return result;
}

export const UAV_SVG = `<svg viewBox="0 0 80 80" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <!-- Fuselage -->
  <path d="M40 10 C38 10 37 13 37 20 L37 45 C37 49 38 52 38.5 64 L36 67 L37 68 L40 66 L43 68 L44 67 L41.5 64 C42 52 43 49 43 45 L43 20 C43 13 42 10 40 10 Z" />
  <!-- Forward Sensor Pod (FLIR Ball) -->
  <circle cx="40" cy="13" r="2.2" />
  <!-- High Aspect Ratio Slender Wings -->
  <path d="M37 32 L3 37 C1.5 37.2 1 38 2 38.5 L37 35 Z" />
  <path d="M43 32 L77 37 C78.5 37.2 79 38 78 38.5 L43 35 Z" />
  <!-- Inverted V-Tail -->
  <path d="M38.5 62 L28 72 C27 73 27.5 73.5 29 73 L39.5 65 Z" />
  <path d="M41.5 62 L52 72 C53 73 52.5 73.5 51 73 L40.5 65 Z" />
  <!-- Winglets / Wingtips -->
  <path d="M2 36.5 L2 39.5 L3.5 38 Z" />
  <path d="M78 36.5 L78 39.5 L76.5 38 Z" />
</svg>`;

export const BALLOON_SVG = `<svg viewBox="0 0 80 80" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <circle cx="40" cy="34" r="22" />
  <path d="M34 54 L38 64 L42 64 L46 54 Z" />
  <rect x="37" y="65" width="6" height="5" rx="1" />
</svg>`;

export const GLIDER_SVG = `<svg viewBox="0 0 80 80" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M39 12 C38.5 12 38 14 38 24 L38 52 C38 58 39 70 40 72 C41 70 42 58 42 52 L42 24 C42 14 41.5 12 40 12 Z" />
  <path d="M38 26 L2 29 L2 30.5 L38 28.5 Z" />
  <path d="M42 26 L78 29 L78 30.5 L42 28.5 Z" />
  <path d="M36 68 L26 70 L26 71 L38 69.5 Z" />
  <path d="M44 68 L54 70 L54 71 L42 69.5 Z" />
</svg>`;

/**
 * Returns the raw SVG markup for a given aircraft type code or plane object.
 * Follows a 5-tier resolution hierarchy:
 * 0. Dedicated Drone / UAV / Balloon / Glider classification
 * 1. Exact match in bundled icons
 * 2. High-precision ICAO alias map
 * 3. Family / Category prefix heuristic (Helicopter, Turboprop, Jet, Fighter)
 * 4. Generic silhouette fallback
 */
export function getAircraftIconSvg(aircraftCode = '') {
  let code = '';
  let isDrone = false;
  let category = '';

  if (typeof aircraftCode === 'object' && aircraftCode !== null) {
    code = String(aircraftCode.aircraft_code || aircraftCode.code || aircraftCode.type || '').trim().toUpperCase();
    isDrone = Boolean(aircraftCode.is_drone);
    category = String(aircraftCode.category || aircraftCode.aircraft_category || '').toLowerCase();
  } else {
    code = String(aircraftCode || '').trim().toUpperCase();
  }

  // 0. Dedicated Drones / Balloons / Gliders
  if (isDrone || category === 'drone' || ['DRON', 'UAV', 'MQ9', 'RQ4', 'TB2', 'HERM', 'SCAN'].includes(code)) {
    if (category === 'balloon' || ['BALL', 'HBAL'].includes(code)) {
      return normalizeViewBox(BALLOON_SVG);
    }
    if (category === 'glider' || ['GLID'].includes(code)) {
      return normalizeViewBox(GLIDER_SVG);
    }
    return normalizeViewBox(UAV_SVG);
  }
  if (category === 'balloon' || ['BALL', 'HBAL'].includes(code)) {
    return normalizeViewBox(BALLOON_SVG);
  }
  if (category === 'glider' || ['GLID'].includes(code)) {
    return normalizeViewBox(GLIDER_SVG);
  }

  let rawSvg = '';

  if (!code) {
    rawSvg = iconsByCode[FALLBACK_CODE] || '';
  } else if (iconsByCode[code]) {
    // 1. Exact match
    rawSvg = iconsByCode[code];
  } else if (ICAO_ALIASES[code] && iconsByCode[ICAO_ALIASES[code]]) {
    // 2. High-precision alias dictionary
    rawSvg = iconsByCode[ICAO_ALIASES[code]];
  } else if (/^(EC|AS|H1|H2|BK|AW|A13|A16|A18|B4|B2|B5)/.test(code)) {
    // 3. Family Heuristic Prefix Matching - Helicopters
    rawSvg = iconsByCode['EC45'] || iconsByCode[FALLBACK_CODE] || '';
  } else if (/^(S7|S9|UH|H6)/.test(code)) {
    rawSvg = iconsByCode['H60'] || iconsByCode[FALLBACK_CODE] || '';
  } else if (/^(R2|R4|R6)/.test(code)) {
    rawSvg = iconsByCode['R44'] || iconsByCode[FALLBACK_CODE] || '';
  } else if (code.startsWith('A3') && iconsByCode['A320']) {
    // Airbus Family
    rawSvg = iconsByCode['A320'];
  } else if (code.startsWith('B7') && iconsByCode['B738']) {
    // Boeing Family
    rawSvg = iconsByCode['B738'];
  } else if (code.startsWith('AT') && iconsByCode['AT75']) {
    // ATR Family
    rawSvg = iconsByCode['AT75'];
  } else if (code.startsWith('E') && iconsByCode['E195']) {
    // Embraer Family
    rawSvg = iconsByCode['E195'];
  } else if (code.startsWith('C1') && iconsByCode['C172']) {
    // Cessna Light Aircraft
    rawSvg = iconsByCode['C172'];
  } else if (/^(GL|GA|LJ|CL|FA|H2)/.test(code) && iconsByCode['GLF6']) {
    // Business Jets (Gulfstream, Learjet, Falcon)
    rawSvg = iconsByCode['GLF6'];
  } else if (/^(F|SU|MIG|JAS|T5)/.test(code) && iconsByCode['F16']) {
    // Military Fighters
    rawSvg = iconsByCode['F16'];
  } else {
    // 4. Universal Fallback
    rawSvg = iconsByCode[FALLBACK_CODE] || '';
  }

  return normalizeViewBox(rawSvg);
}

/**
 * Returns definition { svg } for backward compatibility
 */
export function getAircraftIcon(planeOrCode = '') {
  const svg = getAircraftIconSvg(planeOrCode);
  return { svg };
}
